import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiIdCombobox } from '@/components/ui/multi-id-combobox';
import { useIncidentRoster } from 'components/IAP/hooks/incidentRoster/useIncidentRoster';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import { useGetPositionsForIncident } from 'hooks/incident/useRoster';
import {
  NotificationPriority,
  NotificationType,
  useCreateNotification,
} from 'hooks/useNotification';
import { buildTaskOpenAppMessage } from 'components/MapPage/CorporateViewTabs/NotificationsTab/appMessageTaskLinks';

type TaskApproveExecutionDialogProps = {
  open: boolean;
  actionId: string;
  taskName?: string;
  defaultName?: string;
  /** Dialog title and notify prefix; defaults cover the generic approve step. */
  title?: string;
  onOpenChange: (open: boolean) => void;
  /** Stamp the active Approve Execution signature with the typed name. */
  onConfirmSign: (name: string) => void | Promise<void>;
  confirmPending?: boolean;
};

/**
 * Approve Execution (GS or BD): typed name plus optional notify recipients.
 */
export function TaskApproveExecutionDialog({
  open,
  actionId,
  taskName = '',
  defaultName = '',
  title = 'Approve Execution',
  onOpenChange,
  onConfirmSign,
  confirmPending = false,
}: TaskApproveExecutionDialogProps) {
  const incidentId = useActualIncidentId();
  const { data: roster = [] } = useIncidentRoster();
  const { data: positions = [] } = useGetPositionsForIncident();
  const notify = useCreateNotification();
  const [name, setName] = useState(defaultName);
  const [userIds, setUserIds] = useState<string[]>([]);
  const [positionIds, setPositionIds] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    setName(defaultName);
    setUserIds([]);
    setPositionIds([]);
  }, [open, defaultName]);

  const userOptions = useMemo(
    () =>
      roster
        .filter((member) => member.active !== 0 && member.userId)
        .map((member) => ({
          value: member.userId,
          label: member.userName || member.emailAddress || member.userId,
        })),
    [roster]
  );

  const positionOptions = useMemo(
    () =>
      positions
        .filter((position) => !position.isResourcePosition)
        .map((position) => ({
          value: position.id,
          label: position.name,
        })),
    [positions]
  );

  const confirm = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;

    await onConfirmSign(trimmed);

    const fromPositions = roster
      .filter((member) =>
        member.incidentPositions.some((position) =>
          positionIds.includes(position.positionId)
        )
      )
      .map((member) => member.userId);
    const recipients = [...new Set([...userIds, ...fromPositions])].filter(
      Boolean
    );

    if (incidentId && recipients.length > 0) {
      const path = `/iap/${incidentId}?openTab=Actions&openAction=${actionId}`;
      await notify.mutateAsync({
        incidentId,
        acknowledgementRequired: false,
        autoArchiveDate: null,
        priority: NotificationPriority.INFO,
        emailSubject: null,
        emailMessage: null,
        smsMessage: null,
        teamsMessage: null,
        phoneMessage: null,
        appMessage: buildTaskOpenAppMessage({
          prefix: `${title}.`,
          taskName,
          path,
        }),
        userIds: recipients,
        teamIds: [],
        sourceItemId: null,
        sourceItemType: null,
        scheduledDate: null,
        notificationTypes: [NotificationType.IN_APP],
        aors: [],
      });
    }

    setUserIds([]);
    setPositionIds([]);
    onOpenChange(false);
  };

  const pending = notify.isPending || confirmPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark tw:sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="tw:flex tw:flex-col tw:gap-3">
          <div>
            <Label htmlFor="approve-execution-name" className="tw:mb-1">
              Name
            </Label>
            <Input
              id="approve-execution-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              autoFocus
            />
          </div>
          <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
            Optionally notify others that execution was approved.
          </p>
          <div>
            <div className="tw:mb-1 tw:text-xs tw:font-medium tw:text-muted-foreground">
              Users
            </div>
            <MultiIdCombobox
              options={userOptions}
              value={userIds}
              onChange={setUserIds}
              placeholder="Select users…"
            />
          </div>
          <div>
            <div className="tw:mb-1 tw:text-xs tw:font-medium tw:text-muted-foreground">
              Positions
            </div>
            <MultiIdCombobox
              options={positionOptions}
              value={positionIds}
              onChange={setPositionIds}
              placeholder="Select positions…"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={pending || !name.trim()}
            onClick={() => void confirm()}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
